import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Table, Tooltip, message } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { DownloadOutlined, FileTextOutlined, SearchOutlined } from '@ant-design/icons';
import type { EraListItemVo, EraListVo, TEraSortBy } from 'types/insuranceBilling';
import { getBillingClaimPayers, getBillingEraList } from 'api/insuranceBilling';
import { formatDateMDY, formatMoney } from './statusConfig';
import RemittanceDoc, { NO_ERA_PDF_MSG, NO_ERA_PDF_TIP, downloadEraPdf } from './RemittanceDoc';
import s from './s.module.less';

// payer 后端可排但产品上不放开；date 只做排序不做时间段筛选
const SORTABLE: TEraSortBy[] = ['date', 'claims', 'billed', 'paid'];

// ERA 只有 practice 级数据（无法按 provider 拆分），恒走 practice 端点，不需要 apiScope
type Props = {
    showProviderCol: boolean;
    isSelfProvider: (providerId?: number | null) => boolean;
};

const EraTab = ({ showProviderCol, isSelfProvider }: Props) => {
    const [list, setList] = useState<EraListItemVo[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // 全局搜索（payer 或 check/EFT 号，模糊）
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    // 表头过滤：PAYER（精确多选 + 模糊输入）/ CHECK EFT（模糊输入）
    const [payerSel, setPayerSel] = useState<string[]>([]);
    const [payerSearchInput, setPayerSearchInput] = useState('');
    const [payerSearch, setPayerSearch] = useState('');
    const [payerOptions, setPayerOptions] = useState<string[]>([]);
    const [checkEftInput, setCheckEftInput] = useState('');
    const [checkEft, setCheckEft] = useState('');
    // filterDropdown 开合自控，避免 confirm 触发 Table onChange 覆盖页码
    const [openFilter, setOpenFilter] = useState<'payer' | 'checkEft' | null>(null);
    const [sortBy, setSortBy] = useState<TEraSortBy>('date');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    // 835 汇款单全屏文档页（View）；非 null 即打开，值为 ERA 行 paymentId
    const [openPaymentId, setOpenPaymentId] = useState<number | null>(null);
    // 行内 PDF 下载中的 paymentId（防重复点击 + 按钮 loading）
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const handleDownload = async (record: EraListItemVo) => {
        if (downloadingId != null) return;
        setDownloadingId(record.paymentId);
        const ok = await downloadEraPdf(record.paymentId);
        if (!ok) message.error(NO_ERA_PDF_MSG);
        setDownloadingId(null);
    };

    // 模糊搜索类输入 300ms 防抖；生效时重置回第一页
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPayerSearch(payerSearchInput);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [payerSearchInput]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCheckEft(checkEftInput);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [checkEftInput]);

    // PAYER 表头多选的选项（该 practice claim 里出现过的 payer；ERA 恒为 practice 级）
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res: any = await getBillingClaimPayers('practice');
            if (cancelled) return;
            setPayerOptions((!res?.error && res?.data?.data) || []);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            const res: any = await getBillingEraList({
                page,
                limit: pageSize,
                search: search || undefined,
                payerSearch: payerSearch || undefined,
                checkEft: checkEft || undefined,
                payer: payerSel.length ? payerSel.join(',') : undefined,
                sortBy,
                sortDir,
            });
            if (cancelled) return;
            const vo: EraListVo | null = (!res?.error && res?.data?.data) || null;
            setList(vo?.data || []);
            setTotal(vo?.meta?.total || 0);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [page, pageSize, search, payerSearch, checkEft, payerSel, sortBy, sortDir]);

    const handleTableChange = (
        pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<EraListItemVo> | SorterResult<EraListItemVo>[],
    ) => {
        const one = Array.isArray(sorter) ? sorter[0] : sorter;
        if (one?.order && SORTABLE.includes(one.columnKey as TEraSortBy)) {
            setSortBy(one.columnKey as TEraSortBy);
            setSortDir(one.order === 'ascend' ? 'asc' : 'desc');
        } else {
            // 取消排序回到默认 date desc
            setSortBy('date');
            setSortDir('desc');
        }
        setPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
    };

    const sortOrder = (key: TEraSortBy) => {
        if (sortBy !== key) return null;
        return sortDir === 'asc' ? 'ascend' : 'descend';
    };

    const columns = useMemo(() => {
        const cols: ColumnsType<EraListItemVo> = [
            {
                title: 'PAYER',
                key: 'payer',
                dataIndex: 'payer',
                filteredValue: payerSel.length || payerSearch ? ['payer'] : null,
                filterDropdownOpen: openFilter === 'payer',
                onFilterDropdownOpenChange: (open) => setOpenFilter(open ? 'payer' : null),
                filterDropdown: () => (
                    <div className={s.filterMenu}>
                        <div className={s.dateFilterDropdown}>
                            <Input
                                prefix={<SearchOutlined className={s.searchIcon} />}
                                placeholder="Search payer..."
                                value={payerSearchInput}
                                onChange={(e) => setPayerSearchInput(e.target.value)}
                                allowClear
                            />
                        </div>
                        {payerOptions.map((p) => (
                            <label key={p} className={s.filterMenuRow}>
                                <Checkbox
                                    checked={payerSel.includes(p)}
                                    onChange={(e) => {
                                        const next = e.target.checked
                                            ? [...payerSel, p]
                                            : payerSel.filter((v) => v !== p);
                                        setPayerSel(next);
                                        setPage(1);
                                    }}
                                />
                                <span>{p}</span>
                            </label>
                        ))}
                    </div>
                ),
            },
        ];
        if (showProviderCol) {
            cols.push({
                title: 'PROVIDER',
                key: 'provider',
                // 优先用 providers（该支票实际覆盖的医生名单，去重）：多于 1 人 → Multiple providers；
                // 恰好 1 人 → 姓名 + You pill；空（解析不出归属）→ 兜底整页推断的 providerName / '—'
                render: (_: unknown, record: EraListItemVo) => {
                    const providers = record.providers || [];
                    if (providers.length > 1) return <span className={s.mutCell}>Multiple providers</span>;
                    if (providers.length === 1) {
                        return (
                            <span>
                                {providers[0].name || '—'}
                                {isSelfProvider(providers[0].bpProviderId) && <span className={s.youPill}>You</span>}
                            </span>
                        );
                    }
                    return (
                        <span>
                            {record.providerName || '—'}
                            {isSelfProvider(record.providerId) && <span className={s.youPill}>You</span>}
                        </span>
                    );
                },
            });
        }
        cols.push(
            {
                title: 'CHECK / EFT #',
                key: 'checkEft',
                dataIndex: 'checkEft',
                filteredValue: checkEft ? ['checkEft'] : null,
                filterIcon: <SearchOutlined />,
                filterDropdownOpen: openFilter === 'checkEft',
                onFilterDropdownOpenChange: (open) => setOpenFilter(open ? 'checkEft' : null),
                filterDropdown: () => (
                    <div className={s.dateFilterDropdown}>
                        <Input
                            prefix={<SearchOutlined className={s.searchIcon} />}
                            placeholder="Search check / EFT #..."
                            value={checkEftInput}
                            onChange={(e) => setCheckEftInput(e.target.value)}
                            allowClear
                        />
                    </div>
                ),
                render: (v: string) => <span className={s.monoCell}>{v}</span>,
            },
            {
                title: 'DATE',
                key: 'date',
                dataIndex: 'date',
                sorter: true,
                sortOrder: sortOrder('date'),
                render: (v?: string | null) => <span className={s.mutCell}>{formatDateMDY(v)}</span>,
            },
            {
                title: 'CLAIMS',
                key: 'claims',
                dataIndex: 'claims',
                align: 'right',
                sorter: true,
                sortOrder: sortOrder('claims'),
                render: (v: number) => <span className={s.monoCell}>{v}</span>,
            },
            {
                title: 'BILLED',
                key: 'billed',
                dataIndex: 'billed',
                align: 'right',
                sorter: true,
                sortOrder: sortOrder('billed'),
                render: (v?: number | null) => <span className={s.monoCell}>{formatMoney(v)}</span>,
            },
            {
                title: 'PAID',
                key: 'paid',
                dataIndex: 'paid',
                align: 'right',
                sorter: true,
                sortOrder: sortOrder('paid'),
                render: (v?: number | null) => <span className={s.monoCell}>{formatMoney(v)}</span>,
            },
            {
                title: 'ACTION',
                key: 'action',
                fixed: 'right',
                width: 170,
                align: 'center',
                render: (_: unknown, record: EraListItemVo) => (
                    <span className={s.eraActions}>
                        <Button type="link" className={s.viewLink} onClick={() => setOpenPaymentId(record.paymentId)}>
                            View
                        </Button>
                        {/* 后端标记无 PDF：直接置灰，不发请求，悬停说明原因 */}
                        <Tooltip title={record.hasPdf === false ? NO_ERA_PDF_TIP : undefined}>
                            <Button
                                type="link"
                                className={s.viewLink}
                                icon={<DownloadOutlined />}
                                loading={downloadingId === record.paymentId}
                                disabled={record.hasPdf === false}
                                onClick={() => handleDownload(record)}
                            >
                                Download
                            </Button>
                        </Tooltip>
                    </span>
                ),
            },
        );
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showProviderCol,
        downloadingId,
        sortBy,
        sortDir,
        payerSel,
        payerSearch,
        payerSearchInput,
        payerOptions,
        checkEft,
        checkEftInput,
        openFilter,
    ]);

    const hasFilter = !!(search || payerSearch || checkEft || payerSel.length);

    // 无 remittance（且非筛选导致的空结果）→ ERA 空态
    if (!loading && !list.length && !hasFilter) {
        return (
            <div className={s.tablePanel}>
                <div className={s.eraEmpty}>
                    <span className={s.eraEmptyIcon}>
                        <FileTextOutlined />
                    </span>
                    <div className={s.eraEmptyTitle}>No remittance advice yet</div>
                    <div className={s.eraEmptyBody}>
                        When a payer pays a claim, its remittance advice (ERA / 835) shows up here with the full payment
                        breakdown. There is nothing to show until your first claim is paid.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className={s.filterBar}>
                <Input
                    className={s.searchInput}
                    prefix={<SearchOutlined className={s.searchIcon} />}
                    placeholder="Search by payer or check / EFT #..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    allowClear
                />
            </div>
            <div className={s.tablePanel}>
                <Table<EraListItemVo>
                    rowKey="id"
                    columns={columns}
                    dataSource={list}
                    loading={loading}
                    showSorterTooltip={false}
                    onChange={handleTableChange}
                    locale={{ emptyText: 'No remittances match your search.' }}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </div>
            {openPaymentId != null && (
                <RemittanceDoc paymentId={openPaymentId} onClose={() => setOpenPaymentId(null)} />
            )}
        </div>
    );
};

export default EraTab;
